import { TF } from 'react-translator-component';

export const DASH = '—';

export const actorLabel = (row?: any): string => {
  if (!row) {
    return DASH;
  }
  return row.actorName || row.actorEmail || DASH;
};

export const valueCell = (value?: string | null) => {
  const empty = !value || String(value).trim() === '';
  return <span className={empty ? 'value-dash' : undefined}>{empty ? DASH : value}</span>;
};

const humanizeKey = (key: string): string => {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase()
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const INDENT = '  ';

const isPresent = (value: any) => value !== null && value !== undefined;

/** Item de lista em uma linha: "Chave: valor, Chave: valor". */
const inlineItem = (item: any): string => {
  if (!item || typeof item !== 'object') {
    return String(item);
  }
  return Object.keys(item)
    .filter((key) => isPresent(item[key]))
    .map((key) => {
      const value = item[key];
      return `${humanizeKey(key)}: ${typeof value === 'object' ? JSON.stringify(value) : value}`;
    })
    .join(', ');
};

/**
 * Objeto do metadata (snapshot da configuração avançada, values do perfil de preço) no mesmo
 * padrão dos outros campos — chave humanizada e valor cru —, uma chave por linha. Objeto dentro
 * de objeto vem indentado; lista de objetos, um item por linha; nulos ficam de fora.
 */
const objectLines = (value: any, depth: number): string[] => {
  const pad = INDENT.repeat(depth);
  return Object.keys(value)
    .filter((key) => isPresent(value[key]))
    .flatMap((key) => {
      const item = value[key];
      const label = `${pad}${humanizeKey(key)}`;
      if (Array.isArray(item)) {
        const present = item.filter(isPresent);
        if (present.length === 0) {
          return [`${label}: ${DASH}`];
        }
        if (present.every((i) => typeof i !== 'object')) {
          return [`${label}: ${present.join(', ')}`];
        }
        return [`${label}:`, ...present.map((i) => `${pad}${INDENT}- ${inlineItem(i)}`)];
      }
      if (typeof item === 'object') {
        return [`${label}:`, ...objectLines(item, depth + 1)];
      }
      return [`${label}: ${item}`];
    });
};

const metaValue = (value: any): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (Array.isArray(value)) {
    return value.filter(isPresent).map(inlineItem).join(', ');
  }
  if (typeof value === 'object') {
    return objectLines(value, 0).join('\n');
  }
  return String(value);
};

export const metadataEntries = (
  metadata?: string | null,
): { label: string; value: string }[] => {
  if (!metadata) {
    return [];
  }
  let parsed: any;
  try {
    parsed = JSON.parse(metadata);
  } catch (e) {
    return [{ label: '', value: metadata }];
  }
  if (!parsed || typeof parsed !== 'object') {
    return [{ label: '', value: metaValue(parsed) }];
  }
  return Object.keys(parsed).map((key) => ({
    label: humanizeKey(key),
    value: metaValue(parsed[key]),
  }));
};

/**
 * Coluna "Onde": o pk numérico do alvo (estação etc.) ou, para alvos identificados por UUID
 * como o Perfil de preço e a Configuração Avançada, o uuid.
 */
export const targetLabel = (row?: any): string => {
  if (row?.targetPk !== null && row?.targetPk !== undefined) {
    return String(row.targetPk);
  }
  return row?.targetUuid ? String(row.targetUuid) : DASH;
};

const ACTION_TYPE_LABELS: Record<string, string> = {
  USER_TRUSTED_FLAG_CHANGE: 'Trusted user',
  USER_DOC_CHANGE: 'User document',
  WALLET_MANUAL_ADJUSTMENT: 'Wallet balance',
  TRANSACTION_MANUAL_STOP: 'Manual charge stop',
  CHARGE_BOX_TYPE_CHANGE: 'Charger type',
  CHARGE_BOX_FIRMWARE_CHANGE: 'Charger firmware',
  DASH_STATION_EDIT: 'Station edit',
  DOOR_OPENING: 'Connector door opening',
  DYNAMIC_PRICING_EDIT: 'Price profile edit',
  VIRTUAL_GROUP_EDIT: 'Advanced configuration edit',
};

export const changeLabel = (
  actionType?: string | null,
  fallback?: string | null,
): string => {
  const key = actionType ? ACTION_TYPE_LABELS[actionType] : undefined;
  return key ? TF(key) : fallback || DASH;
};
